/**
 * The plain logic behind an app frame: which app to open, what to say when it
 * cannot open, and whether the app and the live stream are connected.
 *
 * Kept apart from the component so it can be tested without a browser.
 */

import {
  BridgeError,
  MARK,
  PROTOCOL,
  type AppChoice,
  type OpenTarget,
} from "@fanos/omnium-bridge";

import { sentence } from "@/lib/api/auth";

export type Role = "operate" | "score";

export function choiceKey(choice: AppChoice): string {
  return `${choice.workflow.code}/${choice.app.code}`;
}

/**
 * The app to open.
 *
 * The one the person asked for by code, if it is offered; otherwise the
 * default for the role this page wants; otherwise anything offered.
 */
export function pickChoice(
  choices: AppChoice[],
  role: Role,
  want: { app?: string | null; workflow?: string | null } = {},
): AppChoice | null {
  if (want.app) {
    const named = choices.find(
      (choice) =>
        choice.app.code === want.app && (!want.workflow || choice.workflow.code === want.workflow),
    );
    if (named) return named;
  }
  return (
    choices.find((choice) => choice.role === role && choice.isDefault) ??
    choices.find((choice) => choice.role === role) ??
    choices.find((choice) => choice.isDefault) ??
    choices[0] ??
    null
  );
}

/** The panel page an app may ask to open. `null` when omnium has no page for it yet. */
export function pathForOpen(target: OpenTarget, ref: string): string | null {
  if (target === "fixture") return `/fixtures/${encodeURIComponent(ref)}`;
  if (target === "integration") return ref ? `/integrations/${encodeURIComponent(ref)}` : null;
  if (target === "run") {
    // A run belongs to an integration: `<integration>/<Scout run id>` or
    // `<integration>:<Scout run id>`. A bare run id has no page to open.
    const match = /^([^/:]+)[/:](.+)$/.exec(ref);
    if (!match) return null;
    return `/integrations/${encodeURIComponent(match[1])}/runs?scoutRun=${encodeURIComponent(match[2])}`;
  }
  return null;
}

export type FrameProblem = {
  kind: "not_set_up" | "update" | "unavailable" | "not_found" | "sign_in" | "other";
  title: string;
  note: string;
  /** Whether trying again could help. */
  retry: boolean;
};

const NOT_ANSWERING = "Check that the admin service is running, then try again.";

/** What to show when an app could not be opened. */
export function problemFor(error: unknown): FrameProblem {
  const code = error instanceof BridgeError ? error.code : null;
  const said = error instanceof Error && error.message ? sentence(error.message) : "";
  switch (code) {
    case "not_allowed":
      if (/sign in/i.test(said)) {
        return {
          kind: "sign_in",
          title: "Sign in to omnium again",
          note: "Your sign-in has ended.",
          retry: false,
        };
      }
      return {
        kind: "not_set_up",
        title: "This app is not set up to open this",
        note: `${said} Map the app to its workflow on the Workflows page.`.trim(),
        retry: false,
      };
    case "not_offered":
      return {
        kind: "not_set_up",
        title: "This app is not set up to open this",
        note: `${said} Map the app to its workflow on the Workflows page.`.trim(),
        retry: false,
      };
    case "update_app":
      return {
        kind: "update",
        title: "This app needs an update",
        note: "It uses an older version of the omnium bridge.",
        retry: false,
      };
    case "not_found":
      return { kind: "not_found", title: "This no longer exists", note: said, retry: false };
    case "unavailable":
    case null: {
      const generic = !said || /not answering/i.test(said) || error instanceof TypeError;
      return {
        kind: "unavailable",
        title: "omnium is not answering",
        note: generic ? NOT_ANSWERING : `${said} ${NOT_ANSWERING}`,
        retry: true,
      };
    }
    default:
      return { kind: "other", title: "Could not open this app", note: said, retry: true };
  }
}

/** A message from the frame: `ready` on this protocol, `ready` on another one, or neither. */
export function readyIn(data: unknown): "ready" | "old" | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as { omnium?: unknown; type?: unknown; protocol?: unknown };
  if (message.omnium !== MARK || message.type !== "ready") return null;
  return message.protocol === PROTOCOL ? "ready" : "old";
}

/** How long after the frame loads an app has to say `ready`. */
export const READY_WAIT_MS = 8_000;

/**
 * - `waiting`: the frame is loading, or has not said `ready` yet.
 * - `connected`: it said `ready` and the host answered.
 * - `update`: it said `ready` on an older protocol.
 * - `silent`: it loaded but never said `ready` — an app still on the old bridge.
 */
export type Handshake = "waiting" | "connected" | "update" | "silent";

/** The live stream of changes: opening, open, dropped and retrying, or no stream (polling). */
export type Stream = "connecting" | "open" | "retrying" | "polling";

export type Live = { tone: "live" | "wait" | "off"; label: string };

/** The live dot: green only when the app is connected and changes can reach it. */
export function liveState(handshake: Handshake, stream: Stream): Live {
  if (handshake === "update" || handshake === "silent") return { tone: "off", label: "Not connected" };
  if (handshake === "waiting") return { tone: "wait", label: "Opening" };
  if (stream === "open") return { tone: "live", label: "Live" };
  if (stream === "polling") return { tone: "live", label: "Checking every 20 s" };
  if (stream === "retrying") return { tone: "wait", label: "Reconnecting" };
  return { tone: "wait", label: "Connecting" };
}

/** A stream event. `data` says what a change touched, so the host re-reads only those watches. */
type SourceListener = (event: { data?: unknown }) => void;

type SourceLike = {
  addEventListener(type: string, listener: SourceListener): void;
  close(): void;
};
type SourceClass = new (url: string) => SourceLike;

/** How long a dropped stream may take to come back before the page says so. */
export const RECONNECT_GRACE_MS = 5_000;

/**
 * An EventSource that also reports when the live stream opens or drops.
 *
 * A drop is only reported when the stream is still down after `graceMs`. The
 * server ends every stream on purpose after a few seconds (a proxy on AWS
 * would cut it at 15 s otherwise) and the browser is back within about a
 * second, carrying on from the last change it saw. Reporting each of those
 * would show "Reconnecting" all the time for a stream that is working.
 *
 * `null` when the browser has none; the host then polls instead.
 */
export function reportingEventSource(
  onState: (stream: Stream) => void,
  Base: SourceClass | undefined = (globalThis as { EventSource?: SourceClass }).EventSource,
  graceMs: number = RECONNECT_GRACE_MS,
): SourceClass | null {
  if (!Base) return null;
  const Source = Base;
  return class ReportingSource implements SourceLike {
    private readonly source: SourceLike;
    private pending: ReturnType<typeof setTimeout> | null = null;

    constructor(url: string) {
      this.source = new Source(url);
      this.source.addEventListener("open", () => {
        this.settle();
        onState("open");
      });
      this.source.addEventListener("error", () => {
        if (this.pending !== null) return;
        this.pending = setTimeout(() => {
          this.pending = null;
          onState("retrying");
        }, graceMs);
      });
    }

    addEventListener(type: string, listener: SourceListener): void {
      this.source.addEventListener(type, listener);
    }

    close(): void {
      this.settle();
      this.source.close();
    }

    private settle(): void {
      if (this.pending !== null) clearTimeout(this.pending);
      this.pending = null;
    }
  };
}
